<template>
  <b-card border-variant="dark" class="rounded-custom-1 border-custom-2">
    <b-row v-if="currentStep!==-1" class="justify-content-center">
      <b-col md="8">
        <div class="mb-5">
          <stepper :steps="steps.map(p => p.label)" :current-step="currentStep" />
        </div>
        <div>
          <keep-alive>
            <component
              :is="steps[currentStep].component"
              :verify-key="verifyKey"
              :submitting="submitting"
              :team="currentTeam"
              @back="back(currentStep)"
              @skip="next(currentStep)"
              @submitted="next(currentStep)"
            />
          </keep-alive>
        </div>
      </b-col>
    </b-row>
    <div v-else class="text-center p-5">
      <b-spinner variant="primary" label="Loading..." />
    </div>
  </b-card>
</template>

<script>
import { mapGetters } from 'vuex'
import Stepper from '@/components/common/Stepper'
import Identity from '@/views/signup/manager/Identity'
import AboutYou from '@/views/signup/manager/AboutYou'
import Contact from '@/views/signup/manager/Contact'
import TeamProfile from '@/views/signup/manager/TeamProfile'
import Invitations from '@/views/signup/manager/Invitations'
import { toFormData } from '@/helpers/helpers'
import { SIGNUP_STEP_TYPE } from '@/helpers/types'

export default {
  name: 'CreateManager',

  computed: {
    ...mapGetters('signup', [
      'aboutyou',
      'contact',
      'teamprofile',
      'invitations'
    ]),

    ...mapGetters([
      'team'
    ]),

    currentTeam() {
      return this.teamId ? this.team(this.teamId) : null
    }
  },

  props: {
    verifyKey: {
      type: String
    },

    from: {
      type: String
    }
  },

  data() {
    return {
      steps: [
        { type: SIGNUP_STEP_TYPE.IDENTITY, label: 'Identity', component: Identity },
        { type: SIGNUP_STEP_TYPE.ABOUTYOU, label: 'About you', component: AboutYou },
        { type: SIGNUP_STEP_TYPE.CONTACT, label: 'Contact', component: Contact },
        { type: SIGNUP_STEP_TYPE.TEAM, label: 'Team Profile', component: TeamProfile },
        { type: SIGNUP_STEP_TYPE.INVITATIONS, label: 'Invitations', component: Invitations }
      ],
      teamId: null,
      submitting: false,
      currentStep: -1
    }
  },

  async mounted() {
    if (this.from === 'google') {
      const token = await this.$store.dispatch('getToken')
      if (token) {
        await this.$store.dispatch('signup/fetchProfileSignUp')
        this.currentStep = 1
      } else {
        this.toError()
      }
    } else {
      if (!this.verifyKey) {
        this.toError()
      } else {
        try {
          await this.$store.dispatch('companySignUpVerify', this.verifyKey)
          this.currentStep = 0
        } catch {
          this.toError()
        }
      }
    }
  },

  methods: {
    toError() {
      // TODO: Need to redirect to error/expiration page with connect to button?
      this.$router.replace({ name: 'ConnectToWorkspace' })
    },

    async onCompleteTeam() {
      const profileData = { ...this.aboutyou, ...this.contact }

      this.submitting = true
      try {
        // Update user profile
        if (Object.keys(profileData).length > 0) {
          await this.$store.dispatch('updateProfileMe', toFormData(profileData))
        }

        // Create/update a team
        const team = toFormData(this.teamprofile)
        if (!this.teamId) {
          this.teamId = await this.$store.dispatch('createTeam', team)
        } else {
          await this.$store.dispatch('updateTeam', { id: this.teamId, data: team })
        }
      } catch {
        this.$bvToast.toast('Unexpected error', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.submitting = false
      }
    },

    async onCompleteInvite() {
      this.submitting = true
      try {
        // Invite users
        if (this.invitations.length > 0) {
          await this.$store.dispatch('inviteUsers', { team: this.teamId, emails: this.invitations })
        }
      } catch {
        this.$bvToast.toast('Unexpected error', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.submitting = false
      }
    },

    onFinish() {
      this.$store.dispatch('signup/clear')
      this.$router.push({ name: 'Home' })
    },

    async next(from) {
      const step = this.steps[from]
      const next = from + 1
      switch (step.type) {
        case SIGNUP_STEP_TYPE.TEAM:
          await this.onCompleteTeam()
          break
        case SIGNUP_STEP_TYPE.INVITATIONS:
          await this.onCompleteInvite()
          break
      }

      if (next === this.steps.length) {
        this.onFinish()
      } else {
        this.currentStep = next
      }
    },

    back(from) {
      if (from > 0) {
        this.currentStep = from - 1
      }
    }
  },

  components: {
    Stepper,
    Identity,
    AboutYou,
    Contact,
    TeamProfile,
    Invitations
  }
}
</script>
